/**
 * Typed client for customers / contacts / leads (CRM).
 *
 * Routes (beam-backend domainRouter.ts + crmRouter.ts):
 *   POST /api/customers              { customer_type, display_name, company_name? } → customer row
 *   GET  /api/customers                                                              → CustomerDto[]
 *   POST /api/contacts               { full_name, email?, phone?, customer_id?, is_primary? } → contact row
 *   GET  /api/leads                                                                  → lead rows
 *   PATCH /api/leads/:id/stage       { stage }
 *
 * New CRM endpoints return raw Prisma rows (snake_case); we type them locally rather than adding a
 * zod DTO for every shape. Bearer token via client. NEVER imports @smartbox/* (mobile invariant 1).
 */
import { get, post, patch } from './client';
import { CustomerDto } from '@beam/api-types';

export interface CreateCustomerRequest {
  customer_type: 'residential' | 'commercial' | 'government';
  display_name: string;
  company_name?: string;
}

export interface CreateContactRequest {
  full_name: string;
  email?: string;
  phone?: string;
  customer_id?: string;
  is_primary?: boolean;
}

export interface CustomerRow {
  id: string;
  customer_type: string;
  display_name: string;
  company_name: string | null;
}

export interface ContactRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  customer_id: string | null;
  is_primary: boolean;
}

export interface LeadRow {
  id: string;
  stage: string;
  customer_id: string | null;
  contact_id: string | null;
  source: string | null;
  created_at: string;
}

/** List customers (camelCase DTO, shared with estimates). */
export async function listCustomers(): Promise<CustomerDto[]> {
  const raw = await get<unknown>('/api/customers');
  return (raw as unknown[]).map((c) => CustomerDto.parse(c));
}

/** Create a customer. */
export async function createCustomer(body: CreateCustomerRequest): Promise<CustomerRow> {
  return post<CustomerRow>('/api/customers', body);
}

// ---------------------------------------------------------------------------
// Per-customer detail + edit (WIRE-02) — GET/PATCH /api/customers/:id.
// The detail is a camelCase DTO from the backend; the update body is snake_case (matches POST
// /customers). Phone/email/name edit the primary contact; address edits the billing address.
// ---------------------------------------------------------------------------

export interface CustomerContact {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  isPrimary: boolean;
}

export interface CustomerAddress {
  id: string;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface CustomerDetail {
  id: string;
  customerType: string;
  displayName: string;
  companyName: string | null;
  notes: string | null;
  defaultTaxExempt: boolean;
  primaryContact: CustomerContact | null;
  billingAddress: CustomerAddress | null;
  contacts: CustomerContact[];
  estimateCount: number;
  jobCount: number;
}

export interface UpdateCustomerRequest {
  display_name?: string;
  company_name?: string | null;
  notes?: string | null;
  customer_type?: 'residential' | 'commercial' | 'government';
  /** Primary-contact name is required (never null); phone/email may be cleared to null. */
  contact_name?: string;
  phone?: string | null;
  email?: string | null;
  address?: { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string };
}

/** Fetch one customer's detail (contacts, billing address, estimate/job counts). */
export async function getCustomer(id: string): Promise<CustomerDetail> {
  return get<CustomerDetail>(`/api/customers/${id}`);
}

/** Update a customer's saved fields (org-scoped + audited server-side). Returns the updated detail. */
export async function updateCustomer(id: string, body: UpdateCustomerRequest): Promise<CustomerDetail> {
  return patch<CustomerDetail>(`/api/customers/${id}`, body);
}

/** Create a contact (optionally attached to a customer). */
export async function createContact(body: CreateContactRequest): Promise<ContactRow> {
  return post<ContactRow>('/api/contacts', body);
}

/** List leads (pipeline). */
export async function listLeads(): Promise<LeadRow[]> {
  return get<LeadRow[]>('/api/leads');
}

/** Set marketing consent (TCPA) for a contact. */
export async function setMarketingConsent(
  contactId: string,
  body: { sms?: boolean; email?: boolean },
): Promise<ContactRow> {
  return patch<ContactRow>(`/api/contacts/${contactId}/consent`, body);
}
